// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
// --- Styles
import styles from "../details-table.module.scss";

/**
 * Custom sortable header component for AG Grid columns.
 * Supports both Redux-based and local state sort management.
 * @param {Object} props - AG Grid header props
 * @param {string} props.colId - Column identifier
 * @param {string} props.page - Page name for Redux sort state lookup
 * @param {string} props.displayName - Header display text
 * @param {Function} props.setSort - Callback to update sort (colId, direction)
 * @param {string} [props.sortedColId] - Currently sorted column (local state)
 * @param {string} [props.sortDirection] - Current sort direction (local state)
 */
export const CustomHeader = (props) => {
  const { userSort } = useSelector((s) => s.users);
  const { pmxDeliveriesSort } = useSelector((s) => s.pmxDeliveries);
  const { pmxPricesAndDatesSort } = useSelector((s) => s.pmxPricesAndDates);
  const { tapJobSort } = useSelector((s) => s.tapJobs);
  const { updateEngineSort } = useSelector((s) => s.updateEngine);
  const { rateCardsSort } = useSelector((s) => s.rateCards);
  const { licJobSort } = useSelector((s) => s.licensingJobs);

  const {
    colId,
    page,
    displayName,
    setSort,
    // Local state props (for pages not using Redux sort)
    sortedColId: propSortedColId,
    sortDirection: propSortDirection,
  } = props;

  // Map page to sort state (Redux-based)
  const sortMap = {
    users: userSort,
    pmxDeliveries: pmxDeliveriesSort,
    pmxPricesAndDates: pmxPricesAndDatesSort,
    tapJobs: tapJobSort,
    updateEngine: updateEngineSort,
    rateCards: rateCardsSort,
    licensingJobs: licJobSort,
  };

  // Use props if available (local state), otherwise use Redux state
  let sortedColId;
  let sortDirection;

  if (propSortedColId !== undefined) {
    // Use local state passed via props
    sortedColId = propSortedColId;
    sortDirection = (propSortDirection || "asc").toLowerCase();
  } else {
    // Use Redux state
    let sortState = sortMap[page];
    if (Array.isArray(sortState)) {
      sortState = sortState.join(",");
    }
    if (typeof sortState === "string") {
      sortState = sortState.split(",");
    } else if (!Array.isArray(sortState)) {
      sortState = [];
    }
    sortedColId = sortState[0];
    sortDirection = (sortState[1] || "asc").toLowerCase();
  }

  const isActive = colId === sortedColId;

  const handleSort = () => {
    if (!isActive) {
      setSort(colId, "asc");
    } else {
      setSort(colId, sortDirection === "asc" ? "desc" : "asc");
    }
  };

  return (
    <button
      className={`${styles.headerCell} ${isActive ? styles.active : ""}`}
      onClick={handleSort}
      type="button"
    >
      {displayName}
      {isActive ? (sortDirection === "desc" ? "↓" : "↑") : ""}
    </button>
  );
};
